import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Gauge, MapPin, Filter, FileSearch, Snowflake, TrendingUp, Puzzle } from 'lucide-react';

const ICONS = {
  gauge: Gauge,
  'map-pin': MapPin,
  filter: Filter,
  'file-search': FileSearch,
  snowflake: Snowflake,
  'trending-up': TrendingUp,
};

export const getPluginIcon = (name) => ICONS[name] || Puzzle;

const STATUS_STYLES = {
  Live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Private beta': 'bg-slate-100 text-slate-600 border-slate-200',
  'Early access': 'bg-amber-50 text-amber-700 border-amber-200',
};

const PluginCard = ({ plugin }) => {
  const Icon = getPluginIcon(plugin.icon);
  const photo = plugin.screenshots && plugin.screenshots[0];

  return (
    <Link
      to={`/plugins/${plugin.slug}`}
      data-testid={`plugin-card-${plugin.slug}`}
      className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-slate-300 hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.18)] transition-all duration-300"
    >
      {/* Visual header: real product screenshot */}
      <div className="relative h-44 overflow-hidden border-b border-slate-100 bg-slate-900">
        {photo && (
          <img
            src={photo.src}
            alt={`${plugin.name} — ${photo.title}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
        <span
          className="absolute left-4 top-4 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/95 backdrop-blur shadow-sm ring-1 ring-slate-900/5"
          style={{ color: plugin.accent }}
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </span>
        <span
          className={`absolute right-4 top-4 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_STYLES[plugin.status] || STATUS_STYLES['Private beta']}`}
        >
          {plugin.status}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
          {plugin.category}
        </div>
        <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight flex items-center gap-2">
          {plugin.name}
          {plugin.verified && <BadgeCheck className="w-4 h-4 text-slate-400" />}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{plugin.tagline}</p>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500 leading-snug pr-3">
            Requested by a <span className="text-slate-700 font-medium">{plugin.requested_by.designation}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 shrink-0">
            View
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PluginCard;
